// WebPushService.js — FCM web push subscribe for Ai PWA

import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { app, auth, httpsCallable, functions } from 'firebaseConfig';

const VAPID_KEY = String(process.env.REACT_APP_FIREBASE_VAPID_KEY || '').trim();
const SW_PATH = '/firebase-messaging-sw.js';

let messagingInstance = null;
let ensureInFlight = null;
let foregroundUnsubscribe = null;

function cleanString(value) {
  return String(value || '').trim();
}

export function isStandaloneDisplayMode() {
  if (typeof window === 'undefined') return false;
  const standaloneMedia = window.matchMedia?.('(display-mode: standalone)')?.matches;
  const iosStandalone = window.navigator?.standalone === true;
  return Boolean(standaloneMedia || iosStandalone);
}

export function getNotificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

async function getMessagingIfSupported() {
  if (messagingInstance) return messagingInstance;
  const supported = await isSupported().catch(() => false);
  if (!supported) return null;
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

async function registerMessagingServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.register(SW_PATH);
}

function showLocalNotification(payload = {}) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }
  const title = cleanString(payload?.notification?.title || payload?.data?.title) || 'Reminder';
  const body = cleanString(payload?.notification?.body || payload?.data?.body);
  const options = {
    body,
    icon: '/logo192.png',
    badge: '/icons/notification-badge.png',
    data: payload?.data || {},
  };
  try {
    // Prefer SW so tap handling matches background path.
    if (navigator.serviceWorker?.ready) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, options);
      }).catch(() => {
        // eslint-disable-next-line no-new
        new Notification(title, options);
      });
      return;
    }
    // eslint-disable-next-line no-new
    new Notification(title, options);
  } catch (_) {
    // Ignore display failures; delivery still counted server-side.
  }
}

/**
 * Request permission (if needed), get FCM token, register with backend.
 * Safe no-op when unsupported / denied / missing VAPID key.
 */
export async function enableWebPushNotifications({
  requestPermission = true,
} = {}) {
  if (!auth.currentUser) {
    return { ok: false, reason: 'unauthenticated' };
  }
  if (!VAPID_KEY) {
    return { ok: false, reason: 'missing_vapid_key' };
  }

  const messaging = await getMessagingIfSupported();
  if (!messaging) {
    return { ok: false, reason: 'unsupported' };
  }

  let permission = getNotificationPermission();
  if (permission === 'unsupported') {
    return { ok: false, reason: 'unsupported' };
  }
  if (permission === 'denied') {
    return { ok: false, reason: 'denied' };
  }
  if (permission !== 'granted') {
    if (!requestPermission) {
      return { ok: false, reason: 'permission_required' };
    }
    permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { ok: false, reason: 'denied' };
    }
  }

  const registration = await registerMessagingServiceWorker();
  if (!registration) {
    return { ok: false, reason: 'service_worker_failed' };
  }

  // Prefer a fresh token after server-side NotRegistered disables.
  try {
    await deleteToken(messaging);
  } catch (_) {
    // Ignore — getToken below still runs.
  }

  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (!cleanString(token)) {
    return { ok: false, reason: 'token_unavailable' };
  }

  const register = httpsCallable(functions, 'registerWebPushToken');
  await register({
    token,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  });

  return { ok: true, token };
}

/**
 * If permission already granted, refresh/register token (no prompt).
 */
export async function ensureWebPushSubscriptionQuietly() {
  if (ensureInFlight) return ensureInFlight;
  ensureInFlight = enableWebPushNotifications({ requestPermission: false })
    .catch((error) => ({ ok: false, reason: 'error', error }))
    .finally(() => {
      ensureInFlight = null;
    });
  return ensureInFlight;
}

/**
 * Show system notifications while the app is open (FCM otherwise only
 * surfaces via the service worker when backgrounded).
 */
export async function startForegroundPushListener() {
  if (foregroundUnsubscribe) return foregroundUnsubscribe;
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};

  foregroundUnsubscribe = onMessage(messaging, (payload) => {
    showLocalNotification(payload);
  });
  return foregroundUnsubscribe;
}

export function stopForegroundPushListener() {
  if (typeof foregroundUnsubscribe === 'function') {
    foregroundUnsubscribe();
  }
  foregroundUnsubscribe = null;
}
